import TabPage, { TabItemProps } from '../../components/dynamic/Tab';
import { SelectedModeRenderer } from './modes/SpeedReadingModeViews';
import checkPermission from '../../utils/permission';
import React, { useEffect, useState } from 'react';
import { ApiService } from '../../services/api';
import SpeedReadingEditor from './EditorPanel';
import SpeedReadingMetronomePanel from './MetronomePanel';
import SpeedReadingUserSetupPanel from './UserSetupPanel';
import SpeedReadingTrainingPanel from './training/SpeedReadingTrainingPanel';
import { useNavigate } from 'react-router-dom';

import {
  fetchProgramCatalog,
  getModeCanonicalKey,
  getModeQueryValue,
  getSharedExerciseSettings,
  isStoryMode,
  type ModeSection,
  hydrateSharedExerciseSettingsFromServer,
  normalizeRecommendedStoryContent,
  type ModeConfig,
  type RecommendedStoryContent,
} from './modes/speedReadingShared';

const PROMOTED_MODE_KEYS = new Set(['okuma-anlama-seviye-testi', 'serbest-okuma', 'gunluk-odevler']);

const SpeedReadingPage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [modeSections, setModeSections] = useState<ModeSection[]>([]);
  const [modes, setModes] = useState<ModeConfig[]>([]);
  const [selectedMode, setSelectedMode] = useState<ModeConfig | null>(null);
  const [selectedModeContent, setSelectedModeContent] = useState<RecommendedStoryContent | null>(null);
  const [modeContentLoading, setModeContentLoading] = useState(false);
  const canViewSpeedReading = checkPermission('speed_reading_list');
  const canEditSpeedReading = (() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('userData') : null;
      const parsed = raw ? JSON.parse(raw) : null;
      return Boolean(parsed?.isSuperAdmin) || checkPermission('speed_reading_update');
    } catch {
      return checkPermission('speed_reading_update');
    }
  })();
  const canManageSpeedReadingUsers = (() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('userData') : null;
      const parsed = raw ? JSON.parse(raw) : null;
      return Boolean(parsed?.isSuperAdmin) || checkPermission('speed_reading_profile_update');
    } catch {
      return checkPermission('speed_reading_profile_update');
    }
  })();
  const canViewSpeedReadingTraining = (() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('userData') : null;
      const parsed = raw ? JSON.parse(raw) : null;
      return Boolean(parsed?.isSuperAdmin) || checkPermission('speed_reading_lesson_list');
    } catch {
      return checkPermission('speed_reading_lesson_list');
    }
  })();

  useEffect(() => {
    void loadProgramCatalog();
    void loadProfile();
  }, []);

  const loadProgramCatalog = async () => {
    try {
      const catalog = await fetchProgramCatalog();
      setModeSections(catalog.sections);
      setModes(catalog.modes);
    } catch (error) {
      console.error('Hızlı okuma katalog verisi alınamadı:', error);
      setModeSections([]);
      setModes([]);
    }
  };

  const loadProfile = async () => {
    try {
      await hydrateSharedExerciseSettingsFromServer();

      const data = await ApiService.getApi('speed-reading/profile');
      setProfile(data);
    } catch (error) {
      console.error('Speed reading profile yüklenemedi:', error);

      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMode = () => {
    setSelectedMode(null);
    setSelectedModeContent(null);
    setModeContentLoading(false);
  };

  const handleGoToModes = () => {
    handleCloseMode();
    navigate('?tab=modes', { replace: true });
  };

  const handleSelectMode = async (mode: ModeConfig) => {
    const requestedMode = getModeQueryValue(mode);
    if (!requestedMode || !isStoryMode(mode)) {
      setSelectedModeContent(null);
      setSelectedMode(mode);

      return;
    };

    setSelectedMode(mode);
    setSelectedModeContent(null);
    setModeContentLoading(true);

    try {
      let articleDifficulty: string | null = null;
      if (mode.canonicalMode === 'sihirli-hiz' || mode.name === 'İşaretli Metin') {
        try {
          const groupingSettings = await ApiService.getApi('speed-reading/exercises/grouping/settings');
          articleDifficulty = groupingSettings?.difficultyLevel || null;
        } catch (error) {
          console.error('İşaretli Metin zorluk ayarı alınamadı:', error);
        }
      }

      const query = new URLSearchParams({
        mode: requestedMode,
        includeAnswers: ['metin-okuma', 'eksiltili-metin', 'anlama-calismalari', 'hedef-yakalama', 'kaybolan-metin', 'sihirli-hiz', 'serbest-okuma', 'odaklanarak-okuma'].includes(mode.canonicalMode || '') ? 'true' : 'false',
      });
      if (mode.canonicalMode === 'eksiltili-metin') {
        const sharedSettings = getSharedExerciseSettings();
        const maxDots = Math.max(1, Math.min(3, Math.round(Number(sharedSettings.incompleteTextMaxDots) || 3)));
        query.set('incompleteTextMaxDots', String(maxDots));
      }
      if (articleDifficulty) query.set('articleDifficulty', articleDifficulty);

      const recommended = await ApiService.getApi(`speed-reading/content/recommended?${query.toString()}`);
      setSelectedModeContent(normalizeRecommendedStoryContent(recommended));
    } catch (error) {
      console.error('Önerilen hızlı okuma içeriği alınamadı, varsayılan içerik kullanılacak:', error);

      setSelectedModeContent(null);
    } finally {
      setModeContentLoading(false);
    }
  };

  const findModeByCanonicalKey = (canonicalKey: string) => (
    modes.find((mode) => getModeCanonicalKey(mode) === canonicalKey) || null
  );

  const modesGridModes = modes.filter((mode) => !PROMOTED_MODE_KEYS.has(getModeCanonicalKey(mode)));

  const renderPromotedMode = (mode: ModeConfig) => (
    <SelectedModeRenderer
      profile={profile}
      modeSections={modeSections}
      modes={modesGridModes}
      selectedMode={mode}
      selectedModeContent={null}
      modeContentLoading={false}
      onCloseMode={handleGoToModes}
      onSelectMode={(nextMode) => {
        void handleSelectMode(nextMode);
      }}
    />
  );

  if (loading) {
    return (
      <div className="h-[calc(100dvh-4rem)] bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-cyan-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 text-sm">Yükleniyor...</p>
        </div>
      </div>
    );
  };

  if (!canViewSpeedReading) {
    return (
      <div className="h-[88vh] bg-gray-50 overflow-hidden flex">
        <div className="flex-1 min-w-0 flex items-center justify-center">
          <div className="text-center text-gray-600">Bu alana erişim yetkiniz bulunmuyor.</div>
        </div>
      </div>
    );
  };


  const tabs: TabItemProps[] = [
    {
      type: 'custom',
      title: 'Egzersiz Modları',
      path: 'modes',
      onClick: handleGoToModes,
      customComponent: (
        <SelectedModeRenderer
          profile={profile}
          modeSections={modeSections}
          modes={modesGridModes}
          selectedMode={selectedMode}
          selectedModeContent={selectedModeContent}
          modeContentLoading={modeContentLoading}
          onCloseMode={handleCloseMode}
          onSelectMode={(mode) => {
            void handleSelectMode(mode);
          }}
        />
      ),
    }
  ];

  if (canViewSpeedReadingTraining) {
    tabs.push({
      type: 'custom',
      title: 'Eğitim',
      path: 'training',
      customComponent: <SpeedReadingTrainingPanel />,
    });
  }

  tabs.push({
    type: 'custom',
    title: 'Metronom',
    path: 'metronome',
    customComponent: <SpeedReadingMetronomePanel />,
  });

  const readingAssessmentMode = findModeByCanonicalKey('okuma-anlama-seviye-testi');
  if (readingAssessmentMode) {
    tabs.push({
      type: 'custom',
      title: 'Okuma Anlama Seviye Testi',
      path: 'reading-assessment',
      customComponent: renderPromotedMode(readingAssessmentMode),
    });
  }

  const libraryMode = findModeByCanonicalKey('serbest-okuma');
  if (libraryMode) {
    tabs.push({
      type: 'custom',
      title: 'Kütüphanem',
      path: 'library',
      customComponent: renderPromotedMode(libraryMode),
    });
  }

  const homeworkMode = findModeByCanonicalKey('gunluk-odevler');
  if (homeworkMode) {
    tabs.push({
      type: 'custom',
      title: 'Ödevlerim',
      path: 'homework',
      customComponent: renderPromotedMode(homeworkMode),
    });
  }

  if (canEditSpeedReading) {
    tabs.push({
      type: 'custom',
      title: 'Metin Düzenleyici',
      path: 'editor',
      customComponent: <SpeedReadingEditor />,
    });
  }

  if (canManageSpeedReadingUsers) {
    tabs.push({
      type: 'custom',
      title: 'Kullanıcı Düzenleme',
      path: 'user-setup',
      customComponent: <SpeedReadingUserSetupPanel />,
    });
  }

  return <TabPage title="Hızlı Okuma" tabs={tabs} onTitleClick={handleGoToModes} />;
};

export default SpeedReadingPage;
